import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import { Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from 'types';

interface ModalFooterProps {
  onCancel?: () => void;
  onSubmit?: () => void;
  className?: string;
  submitDisabled?: boolean;
  children?: React.ReactNode;
  Actions?: JSX.Element;
}

export const ModalFooter = ({
  onCancel,
  onSubmit,
  submitDisabled = false,
  Actions,
  className,
  children
}: ModalFooterProps) => {
  const classes = useStyles();
  const { t } = useTranslation('ModalFooter');

  let ActionComponents: JSX.Element | null = null;

  if (Actions) ActionComponents = Actions;
  else if (onCancel || onSubmit) {
    ActionComponents = (
      <>
        {!!onCancel && (
          <Button
            aria-label={t('Cancel')}
            variant="outlined"
            color="secondary"
            disableElevation
            onClick={onCancel}
          >
            {t('Cancel')}
          </Button>
        )}
        {!!onSubmit && (
          <Button
            aria-label={t('Save & Close')}
            variant="contained"
            color="primary"
            disableElevation
            onClick={onSubmit}
            disabled={submitDisabled}
          >
            {t('Save & Close')}
          </Button>
        )}
      </>
    );
  }

  return (
    <Grid container component="footer" className={cx(classes.footer, className)}>
      <Grid item>{children}</Grid>
      {ActionComponents && (
        <>
          <Grid item xs />
          <Grid item className={classes.actionContainer}>
            {ActionComponents}
          </Grid>
        </>
      )}
    </Grid>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  footer: {
    alignItems: 'center',
    minHeight: theme.constants.modals.footerHeight,
    borderTop: `1px solid ${theme.palette.divider}`
  },
  actionContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    marginRight: theme.spacing(2),
    '& > *': {
      margin: theme.spacing(1)
    }
  }
}));

export default ModalFooter;
